import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { OZENTYA_SHOP_URL } from "@/lib/social";

import { useLang, LANG_PAIRS } from "@/lib/lang";

const LINKS = [
  { label: "Accueil", en: "Home", to: "/" },
  { label: "Produits", en: "Products", to: "/espace-pro" },
  { label: "Coiffure afro", en: "Afro hair", to: "/coiffure-afro-professionnelle" },
  { label: "Coiffure européenne", en: "European hair", to: "/coiffure-europeenne-professionnelle" },
  { label: "Barber shop", en: "Barber shop", to: "/barber-shop-professionnel" },
  { label: "Marque", en: "Brand", to: "marques" },
  { label: "Avantages pros", en: "Trade benefits", to: "/avantages-professionnels" },
  { label: "Qui sommes-nous ?", en: "About us", to: "/a-propos" },
  { label: "Contact", en: "Contact", to: "/contact" },
];
const tid = (to) => (to === "/" ? "home" : to.replace(/^\//, "").replace(/\//g, "-"));

const LangSwitch = ({ pathname, className = "inline-flex", testid = "lang-switch" }) => {
  const other = LANG_PAIRS[pathname];
  if (!other) return null;
  const en = pathname.startsWith("/en/");
  return (
    <div data-testid={testid} className={`${className} items-center self-start rounded-full border border-[var(--oz-border)] text-xs font-semibold overflow-hidden`}>
      <Link to={en ? other : pathname} data-testid={`${testid}-fr`} className={`px-2.5 py-1.5 transition-colors ${en ? "text-[var(--oz-muted)] hover:text-[var(--oz-text)]" : "bg-[var(--oz-dark)] text-white"}`}>FR</Link>
      <Link to={en ? pathname : other} data-testid={`${testid}-en`} className={`px-2.5 py-1.5 transition-colors ${en ? "bg-[var(--oz-dark)] text-white" : "text-[var(--oz-muted)] hover:text-[var(--oz-text)]"}`}>EN</Link>
    </div>
  );
};

export const Header = () => {
  const { count, setOpen } = useCart();
  const { en, t } = useLang();
  const links = LINKS.map((l) => ({ ...l, label: en ? l.en : l.label }));
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const proLogged = !!localStorage.getItem("oz_pro_token");

  useEffect(() => setMobile(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setMobile(false);
    if (id.startsWith("/")) {
      navigate(id);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-[var(--oz-border)] shadow-[0_4px_30px_-12px_rgba(0,0,0,0.15)]"
          : "bg-transparent"
      }`}
    >
      <div className="px-4 sm:px-8 xl:px-16 max-w-7xl mx-auto flex items-center justify-between gap-3 h-16 sm:h-[72px]">
        <Link
          to="/"
          data-testid="brand-logo"
          className="font-serif text-2xl sm:text-[26px] tracking-tight text-[var(--oz-text)]"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          Ozentya
          <span className="text-[var(--oz-gold)]">.</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5 whitespace-nowrap">
          {links.map((l) => (
            <button
              key={l.to}
              data-testid={`nav-${tid(l.to)}`}
              onClick={() => goTo(l.to)}
              className={`text-[13px] transition-colors relative group ${location.pathname === l.to ? "text-[var(--oz-text)] font-semibold" : "text-[var(--oz-muted)] hover:text-[var(--oz-text)]"}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--oz-gold)] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <LangSwitch pathname={location.pathname} className="hidden sm:inline-flex" />
          {proLogged ? (
            <Link to="/espace-pro/compte" data-testid="header-account" className="inline-flex items-center rounded-full bg-[var(--oz-dark)] text-white ring-2 ring-[var(--oz-gold)] text-xs sm:text-[13px] font-semibold px-3.5 sm:px-5 py-2 sm:py-2.5 hover:bg-black transition-colors whitespace-nowrap">{t("Mon compte pro", "My trade account")}</Link>
          ) : <>
          <Link to="/espace-pro/connexion" data-testid="header-login" className="hidden md:inline-flex whitespace-nowrap text-[13px] font-semibold text-[var(--oz-text)] px-2 hover:text-[var(--oz-gold-hover)] transition-colors">{t("Connexion", "Log in")}</Link>
          <Link to="/espace-pro/inscription" data-testid="header-register-cta"
            className="inline-flex items-center rounded-full bg-[var(--oz-dark)] text-white ring-2 ring-[var(--oz-gold)] text-xs sm:text-[13px] font-semibold px-3.5 sm:px-5 py-2 sm:py-2.5 hover:bg-black transition-colors whitespace-nowrap">
            <span className="sm:hidden">{t("Compte pro", "Trade account")}</span><span className="hidden sm:inline">{t("Créer mon compte professionnel", "Open a trade account")}</span>
          </Link>
          </>}
          <button
            data-testid="cart-drawer-toggle"
            onClick={() => setOpen(true)}
            className="relative p-2.5 rounded-full hover:bg-[var(--oz-bg-2)] transition-colors"
            aria-label={t("Panier", "Cart")}
          >
            <ShoppingBag className="w-5 h-5 text-[var(--oz-text)]" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 bg-[var(--oz-gold)] text-white text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="xl:hidden p-2.5 rounded-full hover:bg-[var(--oz-bg-2)]"
            onClick={() => setMobile((m) => !m)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="xl:hidden bg-white/95 backdrop-blur-md border-t border-[var(--oz-border)] px-4 py-4 flex flex-col gap-1">
          <LangSwitch pathname={location.pathname} className="sm:hidden inline-flex mb-2" testid="lang-switch-mobile" />
          {links.map((l) => (
            <button
              key={l.to}
              data-testid={`nav-mobile-${tid(l.to)}`}
              onClick={() => goTo(l.to)}
              className="text-left py-3 px-2 text-[var(--oz-text)] border-b border-[var(--oz-border)] last:border-0"
            >
              {l.label}
            </button>
          ))}
          <a
            href={OZENTYA_SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-mobile-shop-gamme"
            className="text-left py-3 px-2 text-[var(--oz-muted)]"
          >
            {t("Toute la gamme Ozentya →", "Full Ozentya range →")}
          </a>
          <Link to="/espace-pro/connexion" data-testid="nav-mobile-login" className="py-3 px-2 font-semibold">{t("Connexion espace pro", "Trade login")}</Link>
          <Link to="/packs" data-testid="nav-mobile-packs" className="py-3 px-2 text-sm text-[var(--oz-muted)]">{t("Packs particuliers", "Consumer packs")}</Link>
        </div>
      )}
    </header>
  );
};
